
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { StatsCard } from "@/components/ui/StatsCard";
import { Skeleton } from "@/components/ui/skeleton";
import { CalendarClock, UserCheck, UserPlus, Users } from "lucide-react";
import { useMemo } from "react";
import { BookingData } from "@/types/types";

interface AttendanceHeaderProps {
  bookings: BookingData[];
  isLoading?: boolean;
}

export const AttendanceHeader = ({ bookings, isLoading }: AttendanceHeaderProps) => {
  const { t } = useTranslation("common");
  
  // Use useMemo to calculate stats from bookings
  const stats = useMemo(() => {
    // Debug log to see what data is coming in
    console.log("AttendanceHeader - Received bookings:", bookings?.length || 0);
    
    // Get the total number of upcoming classes
    const upcomingClasses = bookings?.filter(booking => booking.status === 'upcoming')?.length || 0;
    
    // Get the total number of completed classes
    const completedClasses = bookings?.filter(booking => booking.status === 'completed')?.length || 0;
    
    // Get the total number of cancelled classes
    const cancelledClasses = bookings?.filter(booking => booking.status === 'cancelled')?.length || 0;
    
    // Get the total number of unique participants by looking at unique user IDs
    const uniqueUserIds = new Set();
    bookings?.forEach(booking => {
      // If we have a way to extract user IDs from bookings, do it here
      if (booking.id) {
        uniqueUserIds.add(booking.id);
      }
    });
    const uniqueParticipants = uniqueUserIds.size;
    
    return {
      upcomingClasses,
      completedClasses, 
      cancelledClasses,
      uniqueParticipants
    };
  }, [bookings]);
  
  if (isLoading) {
    return (
      <div className="mb-4 sm:mb-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="p-4 sm:p-6">
              <div className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-3 w-24" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="mb-4 sm:mb-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
        <StatsCard
          data-testid="stats-card-upcoming"
          title={t("upcoming")}
          value={stats.upcomingClasses.toString()}
          icon={CalendarClock}
          description={t("upcoming")}
        />
        <StatsCard
          data-testid="stats-card-completed"
          title={t("completed")}
          value={stats.completedClasses.toString()}
          icon={UserCheck}
          description={t("completed")}
        />
        <StatsCard
        data-testid="stats-card-cancelled"
          title={t("cancelled")}
          value={stats.cancelledClasses.toString()}
          icon={UserPlus}
          description={t("cancelled")}
        />
        <StatsCard
        data-testid="stats-card-participants"
          title={t("participants")}
          value={stats.uniqueParticipants.toString()}
          icon={Users}
          description={t("totalParticipants")}
        />
      </div>
    </div>
  );
}
